import type { Entry } from '../../../modules.model';
import { DateHelperService } from '../../../../../../shared/services/date-helper.service';
import { isNaN } from 'lodash-es';

// Pure total calculations, kept out of BaseTimeRegisterationService so the form wrapper can use them
// on any host without booting the week-view service (and its date/query-param side effects).
const DAYS = DateHelperService.DAYS;

/**
 * Pauze rows use a 0% hour-rate (rateProcentage === 0) and must NOT count toward
 * worked-hour totals (total-hours / hours-per-day). They still appear in
 * hours-per-hourrate-type as a separate breakdown line.
 */
function isPauseRow(hoursRow: unknown): boolean {
  const rate = (hoursRow as { 'hour-rate'?: { entry?: { rateProcentage?: unknown } } } | null | undefined)?.[
    'hour-rate'
  ]?.entry?.rateProcentage;
  return rate !== undefined && rate !== null && Number(rate) === 0;
}

export function calculateHours(entry: Entry['entry']) {
  let totalHours = 0;
  entry.hours?.forEach(hoursRow => {
    if (isPauseRow(hoursRow)) {
      return;
    }
    DAYS.forEach(day => {
      const timeValue = Number(hoursRow?.['hours-of-week']?.[day]);
      if (isNaN(timeValue)) {
        return;
      }
      totalHours += timeValue;
    });
  });
  return Number(totalHours.toFixed(2));
}

export function calculateHoursPerDay(entry: Entry['entry']) {
  const totalHoursPerDay = {
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
    summary: '',
  };
  entry.hours?.forEach(hoursRow => {
    if (isPauseRow(hoursRow)) {
      return;
    }
    DAYS.forEach(day => {
      const timeValue = Number(hoursRow?.['hours-of-week']?.[day]);
      if (isNaN(timeValue)) {
        return;
      }
      totalHoursPerDay[day] += +timeValue.toFixed(2);
    });
  });
  totalHoursPerDay.summary = Object.keys(totalHoursPerDay)
    .filter(day => day !== 'summary')
    .map((day, index) => {
      return DateHelperService.DAYS_OF_WEEK_SHORT[index] + ': ' + totalHoursPerDay[day];
    })
    .join(' | ');

  return totalHoursPerDay;
}

export function calculateHoursPerHourRateType(entry: Entry['entry']): string {
  const totalHoursPerHourRateType = {};
  entry.hours?.forEach(hoursRow => {
    const hourRateEntry = hoursRow?.['hour-rate'];
    if (hourRateEntry?.selectedInput == null) {
      return;
    }

    DAYS.forEach(day => {
      const timeValue = Number(hoursRow?.['hours-of-week']?.[day]);
      if (isNaN(timeValue)) {
        return;
      }
      if (totalHoursPerHourRateType[hourRateEntry.selectedInput] === undefined) {
        totalHoursPerHourRateType[hourRateEntry.selectedInput] = 0;
      }
      totalHoursPerHourRateType[hourRateEntry.selectedInput] += +timeValue.toFixed(2);
    });
  });
  return Object.keys(totalHoursPerHourRateType)
    .map(key => {
      return key + ': ' + totalHoursPerHourRateType[key];
    })
    .join(' | ');
}
